import Big from 'big.js';
import React, { Fragment } from 'react';
import {
  Button, ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, Spinner,
  UncontrolledButtonDropdown, UncontrolledTooltip,
} from 'reactstrap';

import AcceptNominatedTradeMutation from 'src/mutations/AcceptNominatedTradeMutation';
import CancelNominatedTradeMutation from 'src/mutations/CancelNominatedTradeMutation';
import RejectNominatedTradeMutation from 'src/mutations/RejectNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';
import {
  DAY_PUBLIC_HOLIDAY, Days, Months, TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
  TRADE_RULE_ACCEPT, TRADE_RULE_CANCEL, TRADE_RULE_REJECT,
  TRADE_RULE_STATE_ACCEPTED, TRADE_RULE_STATE_CANCELLED,
  TRADE_RULE_STATE_CLOSED, TRADE_RULE_STATE_PROPOSED,
  TRADE_RULE_STATE_REJECTED, TRADE_TYPE_COMMUNITY,
  TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED,
  TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import isActive from 'src/util/isActive';
import logger from 'src/util/logger';
import { getMemberNodes } from 'src/util/mainDataBuilder';
import { isFloatValueAndGTEQZero } from 'src/util/math';
import getInitialPriceDisplayValue from 'src/util/price';
import { timeOfDayToDuration } from 'src/util/timeOfUse';

import {
  ActionSpinner, ActionSpinnerWrapper, Step,
} from './tradeRuleConstants';

/**
 * Determines the current state of a trade rule based on the timestamp of the last trade rule
 * action.
 * @param {object} tradeRule - The trade rule object to analyze.
 * @returns {TRADE_RULE_STATE_PROPOSED | TRADE_RULE_STATE_ACCEPTED
 * | TRADE_RULE_STATE_CANCELLED | TRADE_RULE_STATE_REJECTED
 * | TRADE_RULE_STATE_CLOSED | null} The current state of the trade rule,
 * or null if the input is invalid.
 */
export const getTradeRuleState = (tradeRule) => {
  if (!tradeRule) {
    return null;
  }
  const {
    acceptedAt, rejectedAt, cancelledAt, closedAt,
  } = tradeRule;

  if (rejectedAt) {
    return TRADE_RULE_STATE_REJECTED;
  }
  if (cancelledAt) {
    return TRADE_RULE_STATE_CANCELLED;
  }
  if (closedAt) {
    return TRADE_RULE_STATE_CLOSED;
  }
  if (acceptedAt) {
    return TRADE_RULE_STATE_ACCEPTED;
  }
  return TRADE_RULE_STATE_PROPOSED;
};

export const getUserName = (userData) => {
  if (!userData) {
    return null;
  }
  const { email, familyName, givenName } = userData;
  const userName = [givenName, familyName].filter(Boolean).join(' ');
  if (userName) {
    return userName;
  }
  if (email) {
    return email;
  }
  return null;
};

/**
 * Attempt to determine the trade direction based on the buyer and seller details,
 * and a map of meters the user viewing the page has access to.
 * @param {object} buyer
 * @param {object} seller
 * @param {string} actorId the person who proposed the trade rule
 * @returns {object} an object containing the direction of the trade and counterparty
 */
export const getTradeData = (buyer, seller, actorId) => {
  if (actorId === buyer?.user?.uuid) {
    const buyerData = {
      direction: TRADE_DIRECTION_BUY,
      trader: buyer,
      counterParty: seller,
    };
    return buyerData;
  }
  if (actorId === seller?.user?.uuid) {
    const sellerData = {
      direction: TRADE_DIRECTION_SELL,
      trader: seller,
      counterParty: buyer,
    };
    return sellerData;
  }
  return { direction: TRADE_DIRECTION_UNSPECIFIED };
};

export const tradeRuleActions = (buttons) => {
  if (!buttons) {
    return null;
  }
  return (
    <Step className="align-items-center">
      <ActionSpinnerWrapper>
        <ActionSpinner type="grow" />
      </ActionSpinnerWrapper>
      <div className="d-flex ms-3 mt-2 mb-2">
        <ButtonGroup>
          {buttons}
        </ButtonGroup>
      </div>
    </Step>
  );
};

/**
 * Finds a property from a list of user properties based on the given trade point ID.
 * @param {Array<object>} properties - An array of eligible properties.
 * @param {string} tradePointId - The ID of the trade point to find the property for.
 * @returns {object | undefined} - The property object with that trade point id if found,
 * or undefined if not found.
 */
export const getPropertyContainingMeterByTradePointId = (
  properties,
  tradePointId,
) => (properties?.find((property) => property.meters.edges.some(
  (meterEdge) => meterEdge.node.tradePointId === tradePointId,
)));

/**
 * Retrieves a list of eligible properties and a list of eligible trade point IDs
 * from the provided array of `PropertyUser`s.
 * Eligible properties are those that have an active property user, are active
 * and have at least one active meter.
 * Eligible trade point IDs are the eligible properties' trade point IDs.
 * @param {Array<object>} propertyUsers - An array of property users objects.
 * @returns {object} - An object of eligible property and eligible trade point ids array.
 */
export const getEligiblePropertiesAndTradePointIds = (propertyUsers) => {
  const eligibleProperties = [];
  const eligibleTradePointIds = [];

  if (!propertyUsers?.length) {
    return { eligibleProperties, eligibleTradePointIds };
  }

  propertyUsers?.forEach((propertyUserEdge) => {
    const { active: activePropertyUser, property } = propertyUserEdge;
    const { meters, active: activeProperty } = property || {};
    if (!isActive(activePropertyUser)) { return; }
    if (!isActive(activeProperty)) { return; }

    let hasValidTradePoints = false;
    getMemberNodes(meters).forEach(({ active: activeMeter, tradePointId } = {}) => {
      if (!isActive(activeMeter)) { return; }
      eligibleTradePointIds.push(tradePointId);
      hasValidTradePoints = true;
    });

    if (hasValidTradePoints) { eligibleProperties.push(property); }
  });

  return { eligibleProperties, eligibleTradePointIds };
  // TO_DO: append properties accessible via the portfolios.
};

/**
 * Sorts an array of properties by their title in the current locale.
 * @param {Array<object>} properties - An array of property objects.
 * @param {string} locale - The locale used for title comparison.
 * @returns {Array<object>} - The sorted array of property objects.
 */
export const sortPropertiesByTitle = (properties, locale) => properties.sort(
  (a, b) => a.title.localeCompare(b.title, locale),
);

/**
 * Checks if the provided array of clauses is valid. A valid set of clauses must
 * meet the following criteria:
 * - The array must not be empty
 * - The clauses must not overlap in terms of month, day, and time of day
 * - If `ignorePublicHolidays` is true, public holidays will be excluded from the
 *   list of valid days
 * @param {Array<object>} selectedClauses - The array of clauses to validate
 * @param {boolean} ignorePublicHolidays - Whether to exclude public holidays from validation
 * @returns {boolean} - True if the provided clauses are valid, false otherwise
 */
export const areClausesValid = (selectedClauses, ignorePublicHolidays) => {
  if (selectedClauses.length === 0) {
    return false;
  }

  const relevantDays = ignorePublicHolidays
    ? Days.filter((day) => day !== DAY_PUBLIC_HOLIDAY) : Days;

  const combinations = [];
  selectedClauses.forEach(({ monthsOfYear, daysOfWeek, timesOfDay }) => {
    monthsOfYear.forEach((month) => {
      daysOfWeek.forEach((day) => {
        if (ignorePublicHolidays && day === DAY_PUBLIC_HOLIDAY) {
          return;
        }
        timesOfDay.forEach(({ start, finish }) => {
          combinations.push({
            month: Months.indexOf(month),
            day: relevantDays.indexOf(day),
            time: {
              start: timeOfDayToDuration(start).valueOf(),
              finish: timeOfDayToDuration(finish).valueOf(),
            },
          });
        });
      });
    });
  });

  combinations.sort((a, b) => (a.month - b.month || a.day - b.day
      || a.time.start - b.time.start || a.time.finish - b.time.finish));

  for (let i = 1; i < combinations.length; i += 1) {
    const curr = combinations[i];
    const prev = combinations[i - 1];

    const comparison = curr.month - prev.month || curr.day - prev.day
        || curr.time.start - prev.time.finish;

    if (comparison < 0) {
      return false;
    }
  }

  return true;
};

/**
 * Object mapping trade type constants to their corresponding GraphQL field names for querying
 * trade rules.
 *
 * This object provides a centralized mapping between trade type constants and the GraphQL
 * field names used to query trade rules of specific types. The field names are used
 * as aliases in GraphQL queries to fetch filtered trade rule collections from the Meter type.
 * @type {{[key: string]: string}}
 * @private
 */
const TRADE_TYPE_TO_RULES_FIELD = {
  [TRADE_TYPE_CONTRACTED]: 'contractedRules',
  [TRADE_TYPE_NOMINATED]: 'nominatedRules',
  [TRADE_TYPE_COMMUNITY]: 'communityRules',
  [TRADE_TYPE_RESIDUAL]: 'residualRules',
};

/**
 * Helper function to extract trade rules for a property for a trade rule state accepted.
 * @param {object} property - Property object containing meters and rules
 * @param {TRADE_TYPE_CONTRACTED|TRADE_TYPE_NOMINATED|TRADE_TYPE_COMMUNITY
 * |TRADE_TYPE_RESIDUAL} tradeType
 * @returns {object} Object with buy and sell arrays of trade rules
 */
export const getTradeRulesAcceptedForProperty = (property, tradeType) => {
  const { meters } = property || {};
  const rules = { sell: [], buy: [] };

  if (!meters || !tradeType) {
    return rules;
  }
  const rulesField = TRADE_TYPE_TO_RULES_FIELD[tradeType];

  if (!rulesField) {
    return rules;
  }

  meters?.edges?.forEach((meterEdge) => {
    if (meterEdge?.node[rulesField]) {
      meterEdge?.node[rulesField].edges?.forEach((ruleEdge) => {
        const { node: ruleNode } = ruleEdge || {};
        if (
          ruleNode?.state !== TRADE_RULE_STATE_ACCEPTED
          || ruleNode?.tradeType !== tradeType
        ) {
          return;
        }

        if (ruleNode?.seller?.tradePoint?.uuid === meterEdge.node.tradePointId) {
          rules.sell.push({ ...ruleNode, meter: meterEdge.node });
        } else {
          rules.buy.push({ ...ruleNode, meter: meterEdge.node });
        }
      });
    }
  });

  return rules;
};

/**
 * Extract duplicated logic for initializing price-related states from a trade rule clause
 * into a shared helper function.
 * @param {object} intlCurrency
 * @param {object} clause
 * @returns {object} { price, priceDisplay, priceValid }
 */
export const getPriceStateFromClause = (intlCurrency, clause) => {
  const clausePrice = clause?.price ?? null;
  // clausePrice from query params is in Powertracer format ($/Wh) as a raw number.
  // Wrap in Big.js to match handlePriceChange behavior, which always stores
  // price as a Big.js object for the GraphQL mutation.
  const price = clausePrice != null ? Big(clausePrice) : null;
  // Initialize priceDisplay from Powertracer value using shared helper
  const priceDisplay = getInitialPriceDisplayValue(intlCurrency, clausePrice);
  const priceValid = priceDisplay ? isFloatValueAndGTEQZero(priceDisplay) : null;

  return { price, priceDisplay, priceValid };
};

/**
 * Error notification when a trade proposal action fails.
 * @param {string} error
 */
export const handleNominatedTradeMutationFailure = (error) => {
  FlashesStore.flash(FlashesStore.ERROR, error);
};

/**
 *  Success notification when a trade proposal is cancelled.
 * @param {object} intl - React Intl object.
 * @param {object} response
 */
export const handleCancelNominatedTradeMutationSuccess = (intl, response) => {
  const { uuid } = response.cancelNominatedTrade;
  const successMessage = intl.formatMessage(
    {
      id: 'trade_rule.helpers.handle_cancel_nominated_trade_mutation_success.message',
      defaultMessage: 'Cancelled peer-to-peer trade rule proposal with id: {uuid}.',
    },
    { uuid },
  );
  FlashesStore.flash(FlashesStore.SUCCESS, successMessage);
};

/**
 * Success notification when a trade proposal is rejected.
 * @param {object} intl - React Intl object.
 * @param {object} response
 */
export const handleRejectNominatedTradeMutationSuccess = (intl, response) => {
  const { uuid } = response.rejectNominatedTrade;
  const successMessage = intl.formatMessage(
    {
      id: 'trade_rule.helpers.handle_reject_nominated_trade_mutation_success',
      defaultMessage: 'Rejected peer-to-peer trade rule proposal with id: {uuid}.',
    },
    { uuid },
  );
  FlashesStore.flash(FlashesStore.SUCCESS, successMessage);
};

/**
 * Success notification when a trade proposal is accepted.
 * @param {object} intl - React Intl object.
 * @param {object} response
 */
export const handleAcceptNominatedTradeMutationSuccess = (intl, response) => {
  const { uuid } = response.acceptNominatedTrade;
  const successMessage = intl.formatMessage(
    {
      id: 'trade_rule.helpers.handle_accept_nominated_trade_mutation_success',
      defaultMessage: 'Accepted peer-to-peer trade rule proposal with id: {uuid}.',
    },
    { uuid },
  );
  FlashesStore.flash(FlashesStore.SUCCESS, successMessage);
};

/**
 * Trade rule action handler
 * @param {object} intl - React Intl object.
 * @param {object} rule
 * @param {string} action
 * @param {object} opts
 */
export const tradeRuleAction = (intl, rule, action, opts = {}) => {
  const { onFinish, tradePointId } = opts;

  if (!rule || !rule.uuid) {
    onFinish?.();
    return;
  }

  if (!action) {
    onFinish?.();
    return;
  }

  const { id, uuid } = rule;
  const tradeRuleIdSet = { id, uuid };

  switch (action) {
    case TRADE_RULE_CANCEL:
      CancelNominatedTradeMutation(
        tradeRuleIdSet,
        (response) => { handleCancelNominatedTradeMutationSuccess(intl, response); onFinish?.(); },
        (error) => { handleNominatedTradeMutationFailure(error); onFinish?.(); },
      );
      break;
    case TRADE_RULE_REJECT:
      RejectNominatedTradeMutation(
        tradeRuleIdSet,
        (response) => { handleRejectNominatedTradeMutationSuccess(intl, response); onFinish?.(); },
        (error) => { handleNominatedTradeMutationFailure(error); onFinish?.(); },
      );
      break;
    case TRADE_RULE_ACCEPT:
      AcceptNominatedTradeMutation(
        { ...tradeRuleIdSet, tradePointId },
        (response) => { handleAcceptNominatedTradeMutationSuccess(intl, response); onFinish?.(); },
        (error) => { handleNominatedTradeMutationFailure(error); onFinish?.(); },
      );
      break;
    default:
      logger.error(`Unsupported trade rule action: ${action}`);
      onFinish?.();
  }
};

/**
 * Sorts the provided meter nodes by their property title in ascending order.
 * @param {object} a - object to compare.
 * @param {object} a.property - property object.
 * @param {string} a.property.title - property title.
 * @param {object} b - object to compare.
 * @param {object} b.property - property object.
 * @param {string} b.property.title - property title.
 * @returns {number} - This function sort the 2 objects passed in the arguments
 * by the title of the property.
 */
export const sortByPropertyTitle = (a, b) => {
  if (a?.property?.title < b?.property?.title) {
    return -1;
  }
  if (a?.property?.title > b?.property?.title) {
    return 1;
  }

  return 0;
};

/**
 * Builds the array of trade-rule action buttons shared by the customer Show and
 * Proposals views. Returns the raw buttons array so each caller can apply its own
 * wrapper (`tradeRuleActions` vs `ButtonGroup`).
 * @param {object} params - Parameters.
 * @param {object} params.intl - `react-intl` instance used to format labels.
 * @param {object} params.rule - Trade rule object (must be PROPOSED to render buttons).
 * @param {Array<object>} params.meters - Flat array of the viewer's meter nodes.
 * @param {boolean} params.metersLoading - Whether the meters are still being fetched.
 * @param {boolean} [params.isSubmitting] - Whether an action is currently in flight.
 * @param {Function} [params.onActionStartFunc] - Called before a mutation is dispatched.
 * @param {Function} [params.onActionFinishFunc] - Called after a mutation settles.
 * @param {Function} params.resolveActionLabel - `(action) => string` label resolver.
 * @returns {Array<React.ReactElement> | null} The buttons array, or null when not applicable.
 */
export const buildTradeRuleActionButtons = ({
  intl,
  rule,
  meters,
  metersLoading,
  isSubmitting = false,
  onActionStartFunc,
  onActionFinishFunc,
  resolveActionLabel,
}) => {
  if (!intl || !rule || !meters) {
    return null;
  }
  const {
    uuid, state, buyer, seller, actions,
  } = rule;
  if (state !== TRADE_RULE_STATE_PROPOSED || !Array.isArray(actions)) {
    return null;
  }

  const acceptLabel = resolveActionLabel(TRADE_RULE_ACCEPT);
  const noMetersNotice = intl.formatMessage({
    id: 'trade_rule.trade_rule_action_button.no_eligible_meters.message',
    defaultMessage: 'No eligible meters available to accept this trade',
  });

  const counterPartyTradePointId = [
    buyer?.tradePoint?.uuid,
    seller?.tradePoint?.uuid,
  ].filter(Boolean)[0];

  const activeMeterNodes = meters
    .filter(({ active }) => isActive(active))
    .sort(sortByPropertyTitle);

  const options = activeMeterNodes.reduce((acc, el) => {
    const {
      identifier, title, tradePointId, active,
    } = el;
    if (tradePointId === counterPartyTradePointId) { return acc; }
    if (!el.property || !el.property.uuid) { return acc; }
    acc[el.property.uuid] = acc[el.property.uuid] || { property: el.property, tradePoints: [] };
    acc[el.property.uuid].tradePoints.push({
      identifier, title, tradePointId, active,
    });
    return acc;
  }, {});

  // Without a counterparty trade point there is no valid accept flow, so the
  // accept option must be suppressed even if the viewer has eligible meters.
  const hasOptions = Boolean(counterPartyTradePointId) && Object.keys(options).length > 0;

  const buttons = [];

  actions.forEach((action) => {
    if (action === TRADE_RULE_ACCEPT) {
      if (metersLoading) {
        buttons.push(
          <Button key={`${uuid}-${action}`} disabled>
            <Spinner size="sm" />
            {' '}
            {acceptLabel}
          </Button>,
        );
        return;
      }
      if (!hasOptions) {
        const noMetersTargetId = `trade-rule-accept-${uuid}`;
        buttons.push(
          <Fragment key={`${uuid}-${action}`}>
            <span id={noMetersTargetId} className="d-inline-block">
              <Button disabled>{acceptLabel}</Button>
            </span>
            <UncontrolledTooltip target={noMetersTargetId}>
              {noMetersNotice}
            </UncontrolledTooltip>
          </Fragment>,
        );
        return;
      }
      buttons.push(
        <UncontrolledButtonDropdown key={`${uuid}-${action}`}>
          <DropdownToggle caret disabled={isSubmitting}>
            {acceptLabel}
          </DropdownToggle>
          <DropdownMenu>
            {Object.values(options).map((opts) => {
              const { property, tradePoints } = opts;
              return (
                <Fragment key={property.uuid}>
                  <DropdownItem header key={`${uuid}-${action}-${property.uuid}`}>
                    {property.title}
                  </DropdownItem>
                  {tradePoints.map((tp) => (
                    <DropdownItem
                      onClick={() => {
                        onActionStartFunc?.();
                        tradeRuleAction(intl, rule, action, {
                          tradePointId: tp.tradePointId,
                          onFinish: onActionFinishFunc,
                        });
                      }}
                      key={`${uuid}-${action}-${tp.tradePointId}`}
                    >
                      {`${tp.title} (${tp.identifier})`}
                    </DropdownItem>
                  ))}
                </Fragment>
              );
            })}
          </DropdownMenu>
        </UncontrolledButtonDropdown>,
      );
      return;
    }
    buttons.push(
      <Button
        disabled={isSubmitting}
        onClick={() => {
          onActionStartFunc?.();
          tradeRuleAction(intl, rule, action, { onFinish: onActionFinishFunc });
        }}
        key={`${uuid}-${action}`}
      >
        {resolveActionLabel(action)}
      </Button>,
    );
  });

  return buttons;
};
